import { ThemeToggle } from '@/shared/components/MenuBar/ThemeToggle';
import { useEffect, useRef, useState } from 'react';
import { SyncStatus } from '@/shared/components/MenuBar/SyncStatus';
import { useLocation, useParams } from 'react-router-dom';
import CollectionSearchButton from './Sidebar/Search';
import { cx } from 'class-variance-authority';
import { useCurrentPage } from '@/features/pages/hooks/useCurrentPage';
import { usePublicCollectionNote } from '@/features/public/hooks/usePublicCollectionNote';
import { cn } from '@/lib/utils';

const CollectionMenu = ({ enableSync = true }: { enableSync?: boolean }) => {
  const isShared = useLocation().pathname.includes('/shared');
  const { collectionId, pageId } = useParams<{ collectionId: string; pageId: string }>();
  const [showTitle, setShowTitle] = useState(false);
  const lastScrollTop = useRef(0);

  const { data: localPage } = useCurrentPage();
  const { data: publicPage } = usePublicCollectionNote(
    isShared ? pageId : undefined,
    isShared ? collectionId : undefined
  );
  const currentPage = isShared ? publicPage : localPage;

  const menuRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!menuRef.current) return;

      const scrollContainer = menuRef.current.closest('.overflow-y-auto');
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop > lastScrollTop.current && scrollTop > 60) {
        setShowTitle(true);
      } else if (scrollTop <= 60) {
        setShowTitle(false);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    const scrollContainer = menuRef.current?.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <nav ref={menuRef} className="sticky z-50 top-0 w-full bg-background">
      <div
        className={cn(
          'flex items-center justify-end px-3 py-1 sm:pb-2 w-full gap-2',
          showTitle && 'justify-between'
        )}
      >
        {showTitle && (
          <div
            className={cx(
              'ml-10 mt-2 truncate text-md font-medium transition-opacity duration-300 ease-in-out',
              'max-w-[400px] sm:max-w-[720px] md:max-w-none overflow-hidden font-semibold'
            )}
          >
            {currentPage?.title}
          </div>
        )}
        <div className="flex items-center justify-end">
          {isShared && (
            <div className="block md:hidden">
              <CollectionSearchButton collectionId={collectionId || ''} showTitle={false} />
            </div>
          )}
          {enableSync && !isShared && <SyncStatus />}
          <ThemeToggle />
        </div>
      </div>
    </nav>
  );
};

export default CollectionMenu;
